import { Theme } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { DashboardWidgetProps } from "./DashboardWidget";

export type DashboardWidgetStyleProps = Pick<DashboardWidgetProps, "widgetCardShadow">;

// Every widget header row is this tall, whether or not it has controls, so all widget cards start at the same height.
export const widgetHeaderHeight = 32;

// The controls inside a card. Matches the height of a small outlined field, so a DotLegalAutocomplete and a toggle
// side by side line up. Kept separate from the header row so a taller control never moves every widget's title.
export const headerActionHeight = 40;

const useDashboardWidgetStyles = makeStyles((theme: Theme) => {
    const widgetSpacing = theme.spacing(4);
    return createStyles({
        // A column so the content below the controls takes the height the controls leave. Without it the content
        // asks for the card's full height and its last child ends up below the white background.
        card: (props: DashboardWidgetStyleProps) => ({
            height: "100%",
            display: "flex",
            flexDirection: "column",
            padding: widgetSpacing,
            backgroundColor: theme.customPalette.white,
            borderRadius: theme.shape.borderRadius,
            boxShadow: props.widgetCardShadow === false ? "" : "0px 3px 10px #E7E7E7;",
        }),
        // Fixed height so that widgets with header controls line up with the widgets next to them, and so that
        // showing or hiding a control never shifts the card below it.
        headerRow: {
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: theme.spacing(2),
            height: widgetHeaderHeight,
            marginBottom: theme.spacing(1.5),
        },
        header: {
            marginTop: 0,
            marginBottom: 0,
            textTransform: "uppercase",
            fontSize: theme.typography.pxToRem(14),
        },
        // In the flow above the content rather than floating over it, so the controls can never overlap a bar and
        // there is real space between them and the chart. It never shrinks - the content below it gives instead.
        cardActions: {
            display: "flex",
            flexShrink: 0,
            justifyContent: "flex-end",
            alignItems: "center",
            height: headerActionHeight,
            marginBottom: theme.spacing(2),
        },
    });
});

export default useDashboardWidgetStyles;
